import React from "react";
import { Link } from "react-router-dom";
import { useT } from "@/i18n/i18n";
import { SectionHeading } from "@/components/shared";
import { Check, ArrowRight } from "lucide-react";

function useL() {
  const { lang } = useT();
  return (pl, en) => (lang === "pl" ? pl : en);
}

function Hero({ title, lead }) {
  return (
    <section className="border-b border-subtle bg-forestdark">
      <div className="mx-auto max-w-7xl px-4 py-16">
        <h1 className="max-w-3xl font-heading text-3xl font-bold text-white sm:text-4xl lg:text-5xl">{title}</h1>
        {lead && <p className="mt-4 max-w-2xl text-lg text-white/80">{lead}</p>}
      </div>
    </section>
  );
}

function Blocks({ items }) {
  return (
    <div className="mx-auto max-w-4xl space-y-10 px-4 py-16">
      {items.map((b, i) => (
        <div key={i}>
          {b.h && <h2 className="font-heading text-xl font-bold text-charcoal sm:text-2xl">{b.h}</h2>}
          {b.p && <p className="mt-3 leading-relaxed text-mutedtext">{b.p}</p>}
          {b.list && (
            <ul className="mt-4 space-y-2">
              {b.list.map((li, j) => (
                <li key={j} className="flex gap-3 text-charcoal"><Check className="mt-1 h-4 w-4 shrink-0 text-forest" aria-hidden="true" /><span>{li}</span></li>
              ))}
            </ul>
          )}
          {b.note && <p className="mt-4 rounded-md border border-soil/25 bg-soil/5 p-4 text-sm text-soil">{b.note}</p>}
        </div>
      ))}
    </div>
  );
}

export function Mission() {
  const L = useL();
  return (<div>
    <Hero title={L("Misja", "Mission")} lead={L("Stabilność rolnika, żywienie instytucji i regionalna odporność żywnościowa.", "Farmer stability, institutional nutrition and regional food resilience.")} />
    <Blocks items={[
      { h: L("Dlaczego istniejemy", "Why we exist"), p: L("Żywiąca Polska łączy polską produkcję rolną z instytucjami, przetwórstwem i lokalną dystrybucją, aby bezpieczna żywność trafiała do ludzi, a rolnicy zyskiwali przejrzyste rynki i infrastrukturę.", "Żywiąca Polska connects Polish agricultural production with institutions, processing and local distribution so safe food reaches people and farmers gain transparent markets and infrastructure.") },
      { h: L("Nasze filary", "Our pillars"), list: [
        L("Stabilność rolnika i minimalna opłacalna cena zbioru", "Farmer stability and a minimum viable harvest price"),
        L("Żywienie instytucji i krótkie łańcuchy dostaw", "Institutional nutrition and short supply chains"),
        L("Ochrona zbiorów i przetwórstwo żywności niedoskonałej kosmetycznie", "Harvest protection and processing of cosmetically imperfect food"),
        L("Obieg zamknięty nieuniknionych pozostałości i odbudowa gleby", "Circular use of unavoidable residues and soil restoration"),
        L("Przyszły kapitał kontrolowany przez rolników", "Future farmer-controlled capital"),
      ]},
    ]} />
  </div>);
}

export function HowItWorks() {
  const L = useL();
  const { t } = useT();
  return (<div>
    <Hero title={L("Jak to działa", "How it works")} lead={L("Od gospodarstwa, przez centrum regionalne, do instytucji, przetwórstwa, odzysku i gleby.", "From farm, through the regional center, to institutions, processing, recovery and soil.")} />
    <div className="mx-auto max-w-5xl px-4 py-16">
      <div className="flex flex-wrap items-center gap-2">
        {t("flow").map((s, i, arr) => (<React.Fragment key={s}>
          <span className="rounded-md border border-forest/20 bg-forest/5 px-3 py-2 text-sm font-medium text-forest">{s}</span>
          {i < arr.length - 1 && <ArrowRight className="h-4 w-4 text-soil" aria-hidden="true" />}
        </React.Fragment>))}
      </div>
      <div className="mt-10 grid gap-6 md:grid-cols-2">
        {[
          [L("Świeża żywność", "Fresh food"), L("Rolnik rejestruje plon → koordynator dopasowuje do instytucji → dostawa i przejrzyste rozliczenie.", "Farmer registers a crop → coordinator matches to institutions → delivery and transparent settlement.")],
          [L("Klasa przetwórcza", "Processing-grade"), L("Nadwyżki i produkt niedoskonały trafiają do mrożenia, soków, przetworów lub fermentacji.", "Surplus and imperfect produce goes to freezing, juice, preserves or fermentation.")],
          [L("Pozostałości organiczne", "Organic residue"), L("Nieuniknione pozostałości: pasza (gdy dozwolone), biogaz, kompost, nawóz — powrót do gleby.", "Unavoidable residue: feed (where lawful), biogas, compost, fertilizer — returning to soil.")],
          [L("Rozliczenie finansowe", "Financial settlement"), L("Każde potrącenie ma kwotę, powód, osobę odpowiedzialną i historię audytu.", "Every deduction has an amount, reason, responsible person and audit history.")],
        ].map(([h, p]) => (
          <div key={h} className="rounded-md border border-subtle bg-white p-6">
            <h3 className="font-heading text-lg font-bold text-charcoal">{h}</h3>
            <p className="mt-2 text-sm leading-relaxed text-mutedtext">{p}</p>
          </div>
        ))}
      </div>
    </div>
  </div>);
}

export function WhoWeServe() {
  const L = useL();
  const groups = [
    L("Rolnicy", "Farmers"), L("Szkoły i opieka nad dziećmi", "Schools & childcare"), L("Szpitale", "Hospitals"),
    L("Opieka senioralna", "Senior care"), L("Wojsko i odporność cywilna", "Military & civil resilience"),
    L("Zakłady pracy", "Workplaces"), L("Przetwórcy", "Processors"), L("Samorządy", "Municipalities"),
    L("Partnerzy logistyczni", "Logistics partners"), L("Inwestorzy i partnerzy finansujący", "Investors & funding partners"),
  ];
  return (<div>
    <Hero title={L("Kogo wspieramy", "Who we serve")} />
    <div className="mx-auto max-w-6xl px-4 py-16">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {groups.map((g) => (<div key={g} className="rounded-md border border-subtle bg-white p-5 text-sm font-semibold text-charcoal">{g}</div>))}
      </div>
    </div>
  </div>);
}

export function ZeroRot() {
  const L = useL();
  const { t } = useT();
  const hierarchy = [
    L("Świeże spożycie przez ludzi", "Fresh human consumption"),
    L("Kuchnie instytucjonalne", "Institutional kitchens"),
    L("Świeży detal i sprzedaż bezpośrednia", "Fresh retail & direct sales"),
    L("Mrożenie, suszenie, soki, przetwory, fermentacja", "Freezing, drying, juicing, preserving, fermentation"),
    L("Darowizna żywności (gdy zasadne)", "Food donation (where appropriate)"),
    L("Pasza (gdy prawnie dozwolone)", "Animal feed (where legally permitted)"),
    L("Biogaz, kompost, nawóz", "Biogas, compost, fertilizer"),
    L("Kontrolowana utylizacja (ostateczność)", "Controlled disposal (last resort)"),
  ];
  return (<div>
    <Hero title={t("home.zeroRotTitle")} lead={t("home.zeroRot")} />
    <div className="mx-auto max-w-4xl px-4 py-16">
      <h2 className="font-heading text-2xl font-bold text-charcoal">{L("Hierarchia wykorzystania żywności", "Food-use hierarchy")}</h2>
      <ol className="mt-6 space-y-3">
        {hierarchy.map((h, i) => (
          <li key={h} className="flex items-center gap-4 rounded-md border border-subtle bg-white p-4">
            <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-forest font-heading text-sm font-bold text-white">{i + 1}</span>
            <span className="text-sm font-medium text-charcoal">{h}</span>
          </li>
        ))}
      </ol>
      <p className="mt-6 rounded-md border border-soil/25 bg-soil/5 p-4 text-sm text-soil">{L("Sama niedoskonałość kosmetyczna nigdy nie klasyfikuje bezpiecznej żywności jako odpadu.", "Cosmetic imperfection alone never classifies safe food as waste.")}</p>
      <div className="mt-8"><Link to="/rescue-preview" className="inline-flex items-center gap-2 rounded-md bg-forest px-4 py-2.5 text-sm font-semibold text-white hover:bg-forestdark">{L("Zobacz podgląd plonów zagrożonych", "View crop-at-risk preview")} <ArrowRight className="h-4 w-4" /></Link></div>
    </div>
  </div>);
}

export function FarmerProtection() {
  const L = useL();
  return (<div>
    <Hero title={L("Ochrona rolnika", "Farmer protection")} />
    <Blocks items={[
      { list: [
        L("Planowanie przedsezonowe", "Pre-season planning"),
        L("Minimalna opłacalna cena zbioru", "Minimum viable harvest price"),
        L("Przejrzyste rozliczenia", "Transparent settlements"),
        L("Zróżnicowane kanały zbytu i przetwórstwo zapasowe", "Diversified destinations and processing fallback"),
        L("Koncepcja gwarancji zbioru", "Harvest guarantee concept"),
        L("Monitoring integralności rynku i prawdziwe pochodzenie produktu", "Market integrity monitoring and truthful product origin"),
      ]},
      { note: L("Gwarancje obowiązują wyłącznie tam, gdzie są objęte konkretnymi umowami, ilościami, mocą rezerwową i zatwierdzonymi zasadami. Nie obiecujemy prawnie wiążącej krajowej gwarancji ceny.", "Guarantees apply only where covered by specific contracts, quantities, reserve capacity and approved operating rules. We do not promise a legally binding national price guarantee.") },
    ]} />
  </div>);
}

export function Institutional() {
  const L = useL();
  return (<div>
    <Hero title={L("Żywność instytucjonalna", "Institutional food")} lead={L("Sezonowe menu, identyfikowalność, żywienie regionalne i pewny popyt.", "Seasonal menus, traceability, regional sourcing and dependable demand.")} />
    <Blocks items={[
      { h: L("Dostawy dla", "Supply to"), list: [
        L("Dzieci", "Children"), L("Szpitali i rehabilitacji", "Hospitals & rehabilitation"),
        L("Opieki senioralnej", "Senior care"), L("Obiektów wojskowych", "Military facilities"),
        L("Pracowników i stołówek pracowniczych", "Workers and employer canteens"),
      ]},
      { note: L("Nie nazywamy żywności „polską”, jeśli nie potwierdzają tego dane o pochodzeniu.", "We do not call food \"Polish\" unless origin data supports the statement.") },
    ]} />
  </div>);
}

export function Circular() {
  const L = useL();
  return (<div>
    <Hero title={L("Gospodarka obiegu zamkniętego", "Circular economy")} />
    <Blocks items={[
      { list: [
        L("Konserwacja, mrożenie, fermentacja", "Preservation, freezing, fermentation"),
        L("Sok i przecier", "Juice and purée"),
        L("Pasza (gdy prawnie dozwolone)", "Feed (where lawful)"),
        L("Biogaz, kompost, poferment", "Biogas, compost, digestate"),
        L("Powrót do gleby", "Soil return"),
      ]},
      { note: L("Nie twierdzimy, że każdy odpad żywnościowy automatycznie staje się nawozem. Wartości wpływu oznaczamy jako Szacowane, Zgłoszone przez partnera lub Zweryfikowane.", "We do not claim that all food waste can automatically become fertilizer. Impact values are labelled Estimated, Partner-reported or Verified.") },
    ]} />
  </div>);
}

export function Soil() {
  const L = useL();
  return (<div>
    <Hero title={L("Gleba i planowanie upraw", "Soil & crop planning")} lead={L("Moduł przyszłościowy / pilotażowy.", "Future / pilot module.")} />
    <Blocks items={[
      { list: [
        L("Rejestry gleby i daty badań", "Soil records and test dates"),
        L("Dobrowolne rekomendacje płodozmianu", "Voluntary crop-rotation recommendations"),
        L("Rośliny okrywowe i bobowate", "Cover crops and legumes"),
        L("Uzupełnianie materii organicznej", "Organic-matter replacement"),
        L("Alokacja kompostu i pofermentu", "Compost and digestate allocation"),
      ]},
      { note: L("Do przeglądu przez agronoma. Nie jest to automatyczna instrukcja rolnicza.", "For agronomist review. Not an automatic farming instruction.") },
    ]} />
  </div>);
}

export function Governance() {
  const L = useL();
  return (<div>
    <Hero title={L("Ład korporacyjny", "Governance")} lead={L("Proponowany model — nie jest to jeszcze ostateczny fakt prawny.", "Proposed model — not yet a completed legal fact.")} />
    <Blocks items={[
      { h: L("Model rolniczy", "Farmer-rooted model"), list: [
        L("Rolnicy współposiadają regionalne organizacje operacyjne", "Farmers own or participate in regional operating organizations"),
        L("Hussar Holdings może chronić technologię, znaki, standardy i doktrynę założycielską", "Hussar Holdings may protect technology, trademarks, standards and founding doctrine"),
        L("Niezależne audyty i kontrola konfliktu interesów", "Independent audits and conflict-of-interest controls"),
        L("Zabezpieczenia ochrony misji", "Mission-protection provisions"),
        L("Brak kontroli partii politycznej i ukrytych transakcji z podmiotami powiązanymi", "No political-party control and no hidden related-party transactions"),
      ]},
      { note: L("Model oznaczamy jako proponowany do czasu jego przyjęcia przez administratora. Żywiąca Polska to niezależny projekt zainicjowany przez Hussar Holdings LLC; ostateczna polska struktura prawna i spółdzielcza pozostaje w opracowaniu.", "The model is labelled proposed until adopted by an administrator. Żywiąca Polska is an independent project initiated by Hussar Holdings LLC; the final Polish legal and cooperative structure remains under development.") },
    ]} />
  </div>);
}
